import React, { useEffect, useId, useLayoutEffect, useRef } from 'react';
import { X } from 'lucide-react';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Optional line under the title */
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  /** Sticky footer (actions). Always reachable on a phone: it never scrolls away. */
  footer?: React.ReactNode;
  /** Desktop width of the side panel (default 'md' = 28rem; 'full' = full screen on every device) */
  width?: 'md' | 'lg' | 'full';
  /** Close on Escape and on backdrop click (default true) */
  dismissible?: boolean;
  /**
   * Phase 18U U6: 'side' (default) is full screen on phones and a side panel from 768 px;
   * 'bottom' rises from the bottom edge on every device (pickers, quick actions), with a
   * height that fits its content up to 85% of the screen.
   */
  variant?: 'side' | 'bottom';
  /** Element to focus when the sheet opens (default: the first focusable control in the body) */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  /** Test id on the panel */
  testId?: string;
}

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/**
 * Side panel on desktop, full-screen sheet on phones (Phase 9A, plan §6.4), or a bottom sheet
 * (Phase 18U). Focus is trapped inside, Escape closes, and focus returns to the opener.
 * The open/close effect runs only when `open` changes, so a parent that re-renders on every
 * keystroke (entry forms) does not move focus or lose the opener.
 */
const Sheet: React.FC<SheetProps> = ({
  open, onClose, title, subtitle, children, footer, width = 'md', dismissible = true, variant = 'side', initialFocusRef, testId,
}) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);
  const onCloseRef = useRef(onClose);
  const dismissibleRef = useRef(dismissible);
  onCloseRef.current = onClose;
  dismissibleRef.current = dismissible;

  // The opener is read before child effects run (an entry form focuses its first field in a
  // passive effect), so focus can go back to the button that opened the sheet
  useLayoutEffect(() => {
    if (!open) return;
    const active = document.activeElement;
    openerRef.current = active && !panelRef.current?.contains(active) ? active : null;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const panel = panelRef.current;
    const body = panel?.querySelector<HTMLElement>('[data-sheet-body]');
    // A child that already focused its first field (entry forms) keeps that focus
    if (!panel?.contains(document.activeElement)) {
      const target = initialFocusRef?.current || body?.querySelector<HTMLElement>(FOCUSABLE) || panel?.querySelector<HTMLElement>(FOCUSABLE);
      target?.focus();
    }
    const onKeyDown = (event: KeyboardEvent) => {
      // A dialog or sheet opened over (or inside) this one handles its own keys: act only when the
      // focused element's nearest dialog is this panel (or nothing is focused)
      const active = document.activeElement;
      const owner = active && active !== document.body ? active.closest('[role="dialog"], [role="alertdialog"]') : null;
      if (owner && owner !== panel) return;
      if (event.key === 'Escape' && dismissibleRef.current) {
        // Handled: other document listeners (review shortcuts) skip a prevented event
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      (openerRef.current as HTMLElement | null)?.focus?.();
    };
    // Only open/close re-runs this; handlers are read through refs (see the component note)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const bottom = variant === 'bottom';
  const panelClass = bottom
    ? 'relative flex max-h-[85vh] w-full flex-col rounded-t-2xl bg-white shadow-xl md:mx-auto md:max-w-xl'
    : `relative flex h-full w-full flex-col bg-white shadow-xl ${width === 'full' ? '' : width === 'lg' ? 'md:max-w-2xl' : 'md:max-w-md'}`;

  return (
    <div className={`fixed inset-0 z-50 flex ${bottom ? 'items-end justify-center' : 'justify-end'}`}>
      <div
        className="absolute inset-0 bg-black/30"
        aria-hidden="true"
        data-testid="sheet-backdrop"
        onClick={dismissible ? () => onCloseRef.current() : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={testId}
        data-variant={variant}
        className={panelClass}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {bottom && <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-gray-300" aria-hidden="true" />}
        <div className={`flex items-start justify-between gap-3 border-b border-gray-200 px-4 ${bottom ? 'py-2' : 'py-3'}`}>
          <div className="min-w-0">
            <h2 id={titleId} className="truncate text-lg font-semibold text-gray-900">{title}</h2>
            {subtitle && <div className="mt-0.5 text-sm text-gray-600">{subtitle}</div>}
          </div>
          <button
            type="button"
            onClick={() => onCloseRef.current()}
            className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <div data-sheet-body className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer && <div className="border-t border-gray-200 bg-white px-4 py-3">{footer}</div>}
      </div>
    </div>
  );
};

export default Sheet;
